import React, { useState } from "react";
import gql from "graphql-tag";
import { Mutation, Query, QueryResult, MutationFn } from "react-apollo";
import {
  Container,
  FormLabel,
  List,
  ListItem,
  Heading,
  Box,
  Text,
  Button,
  ListIcon,
  Flex,
  Center,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  CSSReset,
  TableContainer,
} from "@chakra-ui/react";
import { CheckCircleIcon, ViewIcon } from "@chakra-ui/icons";
import { useToast } from "@chakra-ui/react";
import UserSelect from "../AssignFavoritesPage/UserSelect";
import { RouteComponentProps } from "react-router";
import { BiLockOpen } from "react-icons/bi";
import { capitalizeAllWords } from "../../lib/utils";
import moment from "moment-timezone";
import { TbListDetails } from "react-icons/tb";

const GET_MODEL = gql`
  query GetModel($id: String) {
    getModel(id: $id) {
      id
      displayName
      launchDate
      roles
      embargoEndDate
    }
  }
`;

const ADD_MODEL_TO_USER = gql`
  mutation AddModelToUser($modelId: String!, $userId: String!) {
    addModelToUser(modelId: $modelId, userId: $userId) {
      user {
        id
        name
        email
        organization
      }
      success
      message
    }
  }
`;

const GET_MODEL_USERS = gql`
  query GetModelUsers($modelId: String!) {
    getModelUsers(modelId: $modelId) {
      id
      email
      name
      organization
    }
  }
`;

const AdminModelAccess = (
  props: RouteComponentProps<MatchParams>
): JSX.Element => {
  const toast = useToast({ duration: 9000, isClosable: true });

  const id = props.match.params.id;
  const [selectedUser, setSelectedUser] = useState<SelectedOption>(null);

  return (
    <Query query={GET_MODEL} variables={{ id }}>
      {({
        data,
        error,
      }: QueryResult<ModelQueryResult, ModelQueryVariables>) => {
        if (error) {
          return <p>Error while getting model.</p>;
        }

        if (data?.getModel) {
          const model = data.getModel;

          const isExclusive = !model.roles.includes("DrillDowns");
          const isEmbargoed =
            !!model.embargoEndDate && moment().isBefore(model.embargoEndDate);

          return (
            <>
              <CSSReset />
              <Container maxW={"1250px"}>
                <Box>
                  <Heading size={"lg"} mb={5}>
                    <ViewIcon w={6} h={6} paddingBottom={1} mr={2} />
                    Model Access ({model.displayName})
                  </Heading>
                  <Box
                    bg="#F7FAFC"
                    border="1px"
                    borderColor="#A0AEC0"
                    borderRadius="5px"
                    p="20px"
                    mb={"8"}
                    display={"flex"}
                  >
                    <Box flex={1}>
                      <Heading size={"md"} mb={5}>
                        <Flex>
                          <Center>
                            <TbListDetails />
                            <Text mb={0} ml={2}>
                              Details
                            </Text>
                          </Center>
                        </Flex>
                      </Heading>
                      <Flex>
                        <FormLabel>ID: </FormLabel>
                        <Text>{model.id}</Text>
                      </Flex>
                      <Flex>
                        <FormLabel>Display Name: </FormLabel>
                        <Text>{model.displayName}</Text>
                      </Flex>
                      <Flex>
                        <FormLabel>Launch Date: </FormLabel>
                        <Text>
                          {moment
                            .tz(model.launchDate, moment.tz.guess())
                            .format("MMMM Do YYYY, h:mm A z")}
                        </Text>
                      </Flex>
                      {isExclusive || isEmbargoed ? (
                        <Flex>
                          <FormLabel>Embargo Status / End Date: </FormLabel>
                          <Text>
                            {isExclusive
                              ? "Exclusive"
                              : moment
                                  .tz(model.embargoEndDate, moment.tz.guess())
                                  .format("MMMM Do YYYY, h:mm A z")}
                          </Text>
                        </Flex>
                      ) : null}

                      <Box>
                        <FormLabel>
                          Permissioned Roles:
                          <Text fontSize={"xs"}>
                            Users with any of the following roles has permission
                            to access this model.
                          </Text>
                        </FormLabel>
                        <List pl={"10px"} pb={0}>
                          {model.roles.map((role, idx) => {
                            return (
                              <ListItem key={idx}>
                                <ListIcon
                                  as={CheckCircleIcon}
                                  color="green.500"
                                />
                                {role}
                              </ListItem>
                            );
                          })}
                        </List>
                      </Box>
                    </Box>
                    <Box
                      flex={1}
                      borderLeft={"1px solid #A0AEC0"}
                      paddingLeft={"20px"}
                    >
                      <FormLabel>
                        Permissioned Users:
                        <Text fontSize={"xs"}>
                          Users with individual access to this model.
                        </Text>
                      </FormLabel>
                      <Query
                        query={GET_MODEL_USERS}
                        variables={{ modelId: model.id }}
                      >
                        {({
                          data,
                          error,
                        }: QueryResult<
                          UsersWithModelAccessQueryResult,
                          UsersWithModelAccessVariables
                        >) => {
                          if (error) {
                            return (
                              <p>
                                Error while getting users with individual access
                                to this model.
                              </p>
                            );
                          }

                          if (data?.getModelUsers) {
                            return (
                              <Box maxH="300px" overflow="auto">
                                <TableContainer>
                                  <Table size="sm">
                                    <Thead>
                                      <Tr>
                                        <Th>Organzation</Th>
                                        <Th>Name</Th>
                                        <Th>Email</Th>
                                      </Tr>
                                    </Thead>
                                    <Tbody>
                                      {data.getModelUsers &&
                                      data.getModelUsers.length ? (
                                        data.getModelUsers.map((user) => {
                                          const name = capitalizeAllWords(
                                            user.name
                                          );

                                          return (
                                            <Tr key={user.id}>
                                              <Td>{user.organization}</Td>
                                              <Td>{name}</Td>
                                              <Td>{user.email}</Td>
                                            </Tr>
                                          );
                                        })
                                      ) : (
                                        <Tr>
                                          <Td colSpan={3} textAlign="center">
                                            No Users Assigned
                                          </Td>
                                        </Tr>
                                      )}
                                    </Tbody>
                                  </Table>
                                </TableContainer>
                              </Box>
                            );
                          }

                          return null;
                        }}
                      </Query>
                    </Box>
                  </Box>
                </Box>
                <Box
                  bg="#F7FAFC"
                  border="1px"
                  borderColor="#A0AEC0"
                  borderRadius="5px"
                  p="20px"
                >
                  <Heading size={"md"} mb={5}>
                    <Flex>
                      <Center>
                        <BiLockOpen />
                        <Text mb={0} ml={2}>
                          Grant Access
                        </Text>
                      </Center>
                    </Flex>
                  </Heading>
                  <UserSelect
                    onUserChange={setSelectedUser}
                    selectedUser={selectedUser}
                  />
                  <Mutation<
                    AddModelToUserMutationResult,
                    AddModelToUserMutationVariables
                  >
                    mutation={ADD_MODEL_TO_USER}
                    update={(cache, { data }) => {
                      const cachedUsers =
                        cache.readQuery<UsersWithModelAccessQueryResult>({
                          query: GET_MODEL_USERS,
                          variables: { modelId: model.id },
                        });

                      const updateList = () => {
                        if (!data?.addModelToUser?.success) {
                          return cachedUsers?.getModelUsers;
                        }

                        if (cachedUsers?.getModelUsers?.length) {
                          return [
                            ...cachedUsers?.getModelUsers,
                            data?.addModelToUser.user,
                          ];
                        }

                        return [data?.addModelToUser.user];
                      };

                      cache.writeQuery({
                        query: GET_MODEL_USERS,
                        variables: { modelId: model.id },
                        data: {
                          getModelUsers: updateList(),
                        },
                      });
                    }}
                  >
                    {(
                      addModelToUser: MutationFn<
                        AddModelToUserMutationResult,
                        AddModelToUserMutationVariables
                      >
                    ) => {
                      return (
                        <Button
                          mt={3}
                          data-cy="allow-model-button"
                          _hover={{ bg: "#004C6E" }}
                          cursor={"pointer"}
                          rightIcon={<ViewIcon />}
                          color="white"
                          border={"none"}
                          bg="#5B646D"
                          onClick={async (e) => {
                            e.preventDefault();

                            if (!selectedUser?.value) {
                              toast({
                                title: "Missing user field.",
                                description:
                                  "A user must be selected to allow access to model.",
                                status: "error",
                              });

                              return;
                            }

                            const { data } = await addModelToUser({
                              variables: {
                                modelId: id,
                                userId: selectedUser.value,
                              },
                            });

                            if (data?.addModelToUser) {
                              toast({
                                title: "Model Access",
                                description: data.addModelToUser.message,
                                status: data.addModelToUser.success
                                  ? "success"
                                  : "error",
                              });
                            }

                            if (data?.addModelToUser.success) {
                              setSelectedUser(null);
                            }
                          }}
                        >
                          Allow Model Access
                        </Button>
                      );
                    }}
                  </Mutation>
                </Box>
              </Container>
            </>
          );
        }

        return null;
      }}
    </Query>
  );
};

export default AdminModelAccess;

interface Model {
  id: string;
  displayName: string;
  launchDate: string;
  roles: string[];
  embargoEndDate: string;
}
interface ModelQueryResult {
  getModel: Model;
}

interface ModelQueryVariables {
  id: string;
}

interface AddModelToUserMutationResult {
  addModelToUser: {
    user: User;
    success: boolean;
    message: string;
  };
}

interface AddModelToUserMutationVariables {
  modelId: string;
  userId: string;
}

interface SelectOption {
  label: string;
  value: string;
}
type SelectedOption = SelectOption | null;

interface MatchParams {
  id: string;
}

interface UsersWithModelAccessVariables {
  modelId: string;
}

interface User {
  id: string;
  email: string;
  name: string;
  organization?: string;
}
interface UsersWithModelAccessQueryResult {
  getModelUsers: User[];
}
